import React, { useEffect, useState, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { OrgContext } from "../../OrgContext";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  XAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { isMSP } from "../../helpers/util_helper";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LoginIcon from "@mui/icons-material/Login";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./MarginManagementDatePicker.css";
import ChartDetailPanel from "./components/ChartDetailPanel";
import CustomerModal from "./components/CustomerModal";
import DeleteCustomerDialog from "./components/DeleteCustomerDialog";
import CustomerAccountsModal from "./components/CustomerAccountsModal";
import {
  getMarginSummary,
  clearMarginState,
} from "../../store/marginManagement/marginSlice";
import { fetchOrgCodes } from "../../store/auth/getOrgScope/orgScopeSlice";
import { getListOfAccts } from "../../store/actions";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import { RESET_STORE } from "../../store/actionTypes";
import { switchToOrganization } from "./utils/organizationSwitcher";

// Shadcn-style input for month picker
const DatePickerInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <TextField
    size="small"
    value={value}
    onClick={onClick}
    ref={ref}
    placeholder={placeholder}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start" sx={{ mr: 0.5 }}>
          <CalendarMonthIcon sx={{ color: "hsl(215.4 16.3% 46.9%)", fontSize: "1.125rem" }} />
        </InputAdornment>
      ),
      readOnly: true,
      sx: {
        backgroundColor: "white",
        borderRadius: "6px",
        fontSize: "0.875rem",
        width: "160px",
        cursor: "pointer",
        color: "hsl(222.2 84% 4.9%)",
        border: "1px solid hsl(214.3 31.8% 91.4%)",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          borderColor: "hsl(214.3 31.8% 85%)",
        },
        "&.Mui-focused": {
          borderColor: "hsl(222.2 84% 4.9%)",
          boxShadow: "0 0 0 2px hsla(222.2, 84%, 4.9%, 0.1)",
          outline: "none",
        },
        "& fieldset": {
          border: "none",
        },
      },
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        paddingRight: 1,
        paddingLeft: 1.25,
        "&.Mui-focused fieldset": { border: "none" },
      },
    }}
  />
));

DatePickerInput.displayName = "DatePickerInput";

const MarginManagement = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { setHasSelectedOrganization } = useContext(OrgContext);
  const hasFetchedOrgScope = useRef(false);
  const [switchComplete, setSwitchComplete] = useState(false);
  const hasFetchedMarginSummary = useRef(false);
  
  // Start/end month selection (payload: start = 1st of start month, end = last day of end month)
  const getFirstOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth(), 1);

  const getLastDayOfMonth = (date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0);

  const MIN_SELECTABLE_YEAR = new Date(2025, 0, 1); // Jan 1, 2025 - cannot select before 2025

  const getDefaultMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1); // 1st of previous month
  };

  const [startMonth, setStartMonth] = useState(getDefaultMonth());
  const [endMonth, setEndMonth] = useState(getDefaultMonth());

  // Reset switchComplete when component mounts to handle normal page loads
  useEffect(() => {
    const isSwitchingOrganization = localStorage.getItem("isSwitchingOrganization") === "true";
    if (!isSwitchingOrganization) {
      setSwitchComplete(false);
    }
  }, []);

  const { marginSummary, isFetchingMarginSummary, fetchMarginSummaryError } =
    useSelector((state) => state.margin);

  const {
    isLoading: isFetchingOrgScope,
    orgCodes,
    errorMessage: fetchOrgScopeError,
    msp,
    mspOrgCode,
  } = useSelector((state) => state.orgScope);

  // Check if user is MSP
  const isMSPUser = isMSP();
  
  // Get user role for button access control
  const userRole = getUserRole();
  const allowedRoles = ["Administrator"];

  // Pagination and search state
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);

  // Chart detail panel state
  const [chartDetailOpen, setChartDetailOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Customer modal state
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  
  // Delete customer dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  // Customer accounts modal state
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [selectedCustomerForAccounts, setSelectedCustomerForAccounts] = useState(null);

  // Get accounts from store
  const accounts = useSelector((state) => state.acctList?.organizations || []);
  const accountsLoading = useSelector((state) => state.acctList?.loading || false);

  // Fetch accounts on mount if not already loaded and not currently loading
  useEffect(() => {
    if ((!accounts || accounts.length === 0) && !accountsLoading) {
      console.log("🚀 Fetching accounts on mount...");
      dispatch(getListOfAccts());
    }
  }, [dispatch, accounts, accountsLoading]);

  // Get all organizations from scope API (excluding MSP org code)
  const getAllOrganizations = () => {
    const orgs = [];

    // Add organization references from scope API, excluding MSP org code
    if (orgCodes && orgCodes.length > 0) {
      orgs.push(...orgCodes.filter((org) => org.orgCode !== mspOrgCode));
    }

    return orgs;
  };

  // Format date to YYYY-MM-DD
  const formatDateForAPI = (date) => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  // Fetch margin summary with date range
  const fetchMarginSummaryWithDates = (start, end) => {
    const params = {
      start_date: formatDateForAPI(start),
      end_date: formatDateForAPI(end),
    };
    console.log("🚀 Calling getMarginSummary API with params:", params);
    dispatch(getMarginSummary(params));
  };

  useEffect(() => {
    // Check if we're in the process of switching organizations
    const isSwitchingOrganization = localStorage.getItem("isSwitchingOrganization") === "true";
    
    if (isSwitchingOrganization && !switchComplete) {
      // Wait for the switch to complete, then reset refs and trigger API calls
      const timeoutId = setTimeout(() => {
        localStorage.removeItem("isSwitchingOrganization");
        // Reset the ref so organization scope will be fetched again
        hasFetchedOrgScope.current = false;
        hasFetchedMarginSummary.current = false; // Reset margin summary ref too
        // Set state to trigger useEffect to run again
        setSwitchComplete(true);
      }, 300);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }

    // Redirect if not MSP user
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }

    // Fetch organization scope only once
    if (!hasFetchedOrgScope.current && (!orgCodes || orgCodes.length === 0)) {
      console.log("Fetching organization scope...");
      hasFetchedOrgScope.current = true;
      dispatch(fetchOrgCodes());
    }

    // Call getMarginSummary API only once on initial load
    if (!hasFetchedMarginSummary.current) {
      hasFetchedMarginSummary.current = true;
      fetchMarginSummaryWithDates(startMonth, getLastDayOfMonth(endMonth));
    }

    return () => {
      // Only clear state on unmount, not on every dependency change
      // dispatch(clearMarginState());
    };
  }, [dispatch, isMSPUser, history, orgCodes, switchComplete]);

  // Log margin summary response when it changes
  useEffect(() => {
    if (marginSummary) {
      console.log("🚀 Margin Summary Response:", marginSummary);
    }
    if (fetchMarginSummaryError) {
      console.log("🚀 Margin Summary Error:", fetchMarginSummaryError);
    }
    if (isFetchingMarginSummary) {
      console.log("🚀 Fetching Margin Summary...");
    }
  }, [marginSummary, fetchMarginSummaryError, isFetchingMarginSummary]);

  // Handle login to organization - switches organization programmatically without showing org-selection page
  const handleLoginToOrganization = async (orgCode, customerName) => {
    console.log("🚀 ~ Logging into organization:", orgCode, customerName);
    
    // Use the helper utility to switch organizations seamlessly
    const success = await switchToOrganization(orgCode, {
      dispatch,
      history,
      setHasSelectedOrganization,
      orgCodes,
      msp,
      mspOrgCode,
      redirectPath: "/costinsight", // Redirect to cloud spend page
    });

    if (!success) {
      console.error("🚀 ~ Failed to switch organization, falling back to org selection page");
      // If switching fails, fallback to org selection page (already handled in switchToOrganization)
    }
  };

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Format percentage values
  const formatPercentage = (value) => {
    return `${value}%`;
  };

  // Format month-year for tooltips (e.g., "2025-06" -> "Jun 2025")
  const formatMonthYear = (value) => {
    if (!value) return '';
    // Convert to string if it's not already
    const valueStr = String(value);
    // Handle both "2025-06" format and extracted month number
    if (valueStr.includes('-')) {
      const [year, month] = valueStr.split('-');
      if (!year || !month) return valueStr;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[parseInt(month) - 1];
      return monthName ? `${monthName} ${year}` : valueStr;
    }
    return valueStr;
  };

  // Get margin trend data for chart - safely handle errors
  const getMarginTrendData = () => {
    try {
      if (!marginSummary?.margin_trend_last_3_months) return [];
      if (typeof marginSummary.margin_trend_last_3_months !== "object") return [];

      return Object.entries(marginSummary.margin_trend_last_3_months).map(
        ([month, value]) => ({
          month: month?.split("-")[1] || month, // Extract month number
          value: value || 0,
          fullMonth: month,
        })
      );
    } catch (error) {
      console.error("Error getting margin trend data:", error);
      return [];
    }
  };

  // Get spend trend data for chart - safely handle errors
  const getSpendTrendData = () => {
    try {
      if (!marginSummary?.data || !Array.isArray(marginSummary.data)) return [];

      const monthlyData = {};
      marginSummary.data.forEach((customer) => {
        if (customer?.last_3_month_customer_spend_cost && Array.isArray(customer.last_3_month_customer_spend_cost)) {
          customer.last_3_month_customer_spend_cost.forEach((spend) => {
            if (spend?.month && typeof spend.amount === "number") {
              if (!monthlyData[spend.month]) {
                monthlyData[spend.month] = 0;
              }
              monthlyData[spend.month] += spend.amount;
            }
          });
        }
      });

      return Object.entries(monthlyData).map(([month, amount]) => ({
        month: month?.split("-")[1] || month, // Extract month number
        amount: amount || 0,
        fullMonth: month,
      }));
    } catch (error) {
      console.error("Error getting spend trend data:", error);
      return [];
    }
  };

  // Get mini chart data for individual customer - safely handle errors
  const getCustomerMiniChartData = (customer) => {
    try {
      if (!customer?.last_3_month_customer_spend_cost || !Array.isArray(customer.last_3_month_customer_spend_cost)) {
        return [];
      }

      return customer.last_3_month_customer_spend_cost
        .filter((spend) => spend?.month && typeof spend.amount === "number")
        .map((spend) => ({
          month: spend.month, // Keep full YYYY-MM format
          amount: spend.amount,
          monthNumber: spend.month?.split("-")[1] || spend.month, // Extract month number for mini charts
        }));
    } catch (error) {
      console.error("Error getting customer mini chart data:", error);
      return [];
    }
  };

  // Get status color for margin status
  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE_AND_UPCOMING":
        return "#059669";
      case "ACTIVE":
        return "#3b82f6";
      case "UPCOMING":
        return "#d97706";
      case "EXPIRED":
        return "#dc2626";
      default:
        return "#6b7280";
    }
  };

  // Filter customers based on search term (search across all relevant fields)
  // Safely handle cases where marginSummary or data might be null/undefined
  const filteredCustomers = React.useMemo(() => {
    try {
      if (!marginSummary || !marginSummary.data || !Array.isArray(marginSummary.data)) {
        return [];
      }
      
      if (!searchTerm) return marginSummary.data;

      const searchLower = searchTerm.toLowerCase();
      return marginSummary.data.filter((customer) => {
        if (!customer) return false;
        return (
          customer.customer_name?.toLowerCase().includes(searchLower) ||
          customer.orgcode?.toLowerCase().includes(searchLower) ||
          customer.margin_status?.toLowerCase().includes(searchLower) ||
          customer.contract_end_date?.toLowerCase().includes(searchLower) ||
          customer.status_note?.toLowerCase().includes(searchLower) ||
          customer.current_margin?.toString().includes(searchTerm) ||
          customer.realized_margin?.toString().includes(searchTerm) ||
          customer.customer_usage?.toString().includes(searchTerm) ||
          customer.aws_usage_list_price?.toString().includes(searchTerm) ||
          customer.msp_wholesale_cost?.toString().includes(searchTerm) ||
          customer.total_customer_spend?.toString().includes(searchTerm)
        );
      });
    } catch (error) {
      console.error("Error filtering customers:", error);
      return [];
    }
  }, [marginSummary, searchTerm]);

  // Get paginated customers - safely handle array operations
  const paginatedCustomers = React.useMemo(() => {
    try {
      if (!Array.isArray(filteredCustomers)) return [];
      return filteredCustomers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
    } catch (error) {
      console.error("Error paginating customers:", error);
      return [];
    }
  }, [filteredCustomers, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle chart detail panel
  const handleOpenChartDetail = (customer) => {
    setSelectedCustomer(customer);
    setChartDetailOpen(true);
  };

  const handleCloseChartDetail = () => {
    setChartDetailOpen(false);
    setSelectedCustomer(null);
  };

  // Handle customer modal
  const handleOpenCustomerModal = () => {
    setCustomerModalOpen(true);
  };

  const handleCloseCustomerModal = () => {
    setCustomerModalOpen(false);
  };

  const handleCustomerSuccess = () => {
    // Refresh margin summary after customer is added
    fetchMarginSummaryWithDates(startMonth, getLastDayOfMonth(endMonth));
    // Also refresh accounts list
    dispatch(getListOfAccts());
  };

  // Handle delete customer
  const handleOpenDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCustomerToDelete(null);
  };

  const handleDeleteSuccess = () => {
    // Refresh margin summary after customer is deleted
    fetchMarginSummaryWithDates(startMonth, getLastDayOfMonth(endMonth));
    // Also refresh accounts list
    dispatch(getListOfAccts());
  };

  // Handle customer accounts modal
  const handleOpenAccountsModal = (customer) => {
    setSelectedCustomerForAccounts(customer);
    setAccountsModalOpen(true);
  };

  const handleCloseAccountsModal = () => {
    setAccountsModalOpen(false);
    setSelectedCustomerForAccounts(null);
  };

  const handleStartMonthChange = (date) => {
    const first = getFirstOfMonth(date);
    const effectiveEnd = first > endMonth ? first : endMonth;
    setStartMonth(first);
    if (first > endMonth) setEndMonth(first);
    hasFetchedMarginSummary.current = false;
    fetchMarginSummaryWithDates(first, getLastDayOfMonth(effectiveEnd));
    hasFetchedMarginSummary.current = true;
  };

  const handleEndMonthChange = (date) => {
    const first = getFirstOfMonth(date);
    const effectiveStart = first < startMonth ? first : startMonth;
    setEndMonth(first);
    if (first < startMonth) setStartMonth(first);
    hasFetchedMarginSummary.current = false;
    fetchMarginSummaryWithDates(effectiveStart, getLastDayOfMonth(first));
    hasFetchedMarginSummary.current = true;
  };

  // Helper function to safely extract error message from any error format
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      // Handle object with error key
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string") return error.message;
      // Handle nested error objects
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      // Try to stringify if it's an object
      try {
        const stringified = JSON.stringify(error);
        // If it's too long, just return a generic message
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  // Get status background color
  const getStatusBgColor = (status) => {
    switch (status) {
      case "ACTIVE_AND_UPCOMING":
        return "#d1fae5";
      case "ACTIVE":
        return "#dbeafe";
      case "UPCOMING":
        return "#fed7aa";
      case "EXPIRED":
        return "#fee2e2";
      default:
        return "#f3f4f6";
    }
  };

  if (!isMSPUser) {
    return null; // Will redirect to dashboard
  }

  return (
    <Box
      sx={{
        padding: "1.5rem 2rem",
        mb: "3rem",
        backgroundColor: "#F9F9F9",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Compact Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.5rem",
                letterSpacing: "-0.025em",
                mb: 0.5,
              }}
            >
              Customer Management
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              Manage and review margin performance for all customer accounts
            </Typography>
          </Box>
          
          {/* Start / End month (payload: start = 1st of start month, end = last day of end month) */}
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              flexWrap: "wrap",
              backgroundColor: "#f8fafc",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ color: "#64748b", fontSize: "0.875rem", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                Start month:
              </Typography>
              <DatePicker
                selected={startMonth}
                onChange={handleStartMonthChange}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                minDate={MIN_SELECTABLE_YEAR}
                maxDate={endMonth}
                placeholderText="Start month"
                customInput={<DatePickerInput placeholder="Start month" />}
                popperClassName="margin-datepicker-popper"
                calendarClassName="margin-datepicker-calendar"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ color: "#64748b", fontSize: "0.875rem", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                End month:
              </Typography>
              <DatePicker
                selected={endMonth}
                onChange={handleEndMonthChange}
                dateFormat="MMM yyyy"
                showMonthYearPicker
                minDate={startMonth}
                maxDate={new Date()}
                placeholderText="End month"
                customInput={<DatePickerInput placeholder="End month" />}
                popperClassName="margin-datepicker-popper"
                calendarClassName="margin-datepicker-calendar"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Loading State */}
      {isFetchingMarginSummary && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
          <Typography
            variant="body2"
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading margin summary...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {fetchMarginSummaryError && (
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #f87171",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchMarginSummaryError)}
          </Typography>
        </Box>
      )}

      {/* Dashboard Content */}
      {marginSummary && !isFetchingMarginSummary && (
        <>
          {/* Compact Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Customer Count */}
            <Grid item xs={6} sm={4} md={2.4}>
              <Card
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  height: 120,
                  "&:hover": {
                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        backgroundColor: "#f3f4f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                      }}
                    >
                      <PeopleIcon sx={{ color: "#000000", fontSize: 16 }} />
                    </Box>
                    <Tooltip
                      title="Total number of sub-organizations under the MSP"
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "0.875rem",
                        }}
                      >
                        Customers
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: "1.75rem",
                      mb: 0.5,
                    }}
                  >
                    {marginSummary?.customer_count ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Realized Margin */}
            <Grid item xs={6} sm={4} md={2.4}>
              <Card
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  height: 120,
                  "&:hover": {
                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        backgroundColor: "#f3f4f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                      }}
                    >
                      <AttachMoneyIcon
                        sx={{ color: "#000000", fontSize: 16 }}
                      />
                    </Box>
                    <Tooltip
                      title="Monthly cumulative MSP margin across all sub-organizations, limited to months where a margin was active"
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "0.875rem",
                        }}
                      >
                        Total MSP Margin
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#000000",
                      fontSize: "1.75rem",
                      mb: 0.5,
                    }}
                  >
                    {formatCurrency(marginSummary?.total_realized_margin_ytd ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Spend YTD */}
            <Grid item xs={6} sm={4} md={2.4}>
              <Card
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  height: 120,
                  "&:hover": {
                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        backgroundColor: "#f3f4f6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1.5,
                      }}
                    >
                      <TrendingUpIcon sx={{ color: "#000000", fontSize: 16 }} />
                    </Box>
                    <Tooltip
                      title="Year-to-date MSP Wholesale Cost across all customers, regardless of whether a margin is active, expired, or upcoming"
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "0.875rem",
                        }}
                      >
                        Total MSP Wholesale Cost
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#000000",
                      fontSize: "1.75rem",
                      mb: 0.5,
                    }}
                  >
                    {formatCurrency(marginSummary?.total_spend_ytd ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Margin Trend Chart */}
            <Grid item xs={6} sm={4} md={2.4}>
              <Card
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  height: 120,
                  "&:hover": {
                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Tooltip
                    title="Monthly cumulative realized margin across all sub-organizations, limited to months where a margin was active"
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "0.875rem",
                        mb: 1,
                      }}
                    >
                      3-month Margin Performance
                    </Typography>
                  </Tooltip>
                  <Box sx={{ height: 60 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getMarginTrendData()}>
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                            fontSize: "12px",
                          }}
                          formatter={(value) => [
                            formatCurrency(value),
                            "Margin",
                          ]}
                          labelFormatter={(label, payload) => {
                            // Use fullMonth from payload if available, otherwise format the label
                            const fullMonth = payload && payload[0]?.payload?.fullMonth;
                            const valueToFormat = fullMonth || label || '';
                            return formatMonthYear(valueToFormat);
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#000000"
                          strokeWidth={1.5}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Spend Trend Chart */}
            <Grid item xs={6} sm={4} md={2.4}>
              <Card
                sx={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  height: 120,
                  "&:hover": {
                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Tooltip
                    title="Last three months of professional services charged"
                    arrow
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "0.875rem",
                        mb: 1,
                      }}
                    >
                      3-month customer spend cost by all customers
                    </Typography>
                  </Tooltip>
                  <Box sx={{ height: 60 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={getSpendTrendData()}>
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
                            fontSize: "12px",
                          }}
                          formatter={(value) => [
                            formatCurrency(value),
                            "Customer spend",
                          ]}
                          labelFormatter={(label, payload) => {
                            // Use fullMonth from payload if available, otherwise format the label
                            const fullMonth = payload && payload[0]?.payload?.fullMonth;
                            const valueToFormat = fullMonth || label || '';
                            return formatMonthYear(valueToFormat);
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="amount"
                          stroke="#000000"
                          strokeWidth={1.5}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Compact Customer Details Table */}
          <Card
            sx={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "0.875rem",
                  }}
                >
                  Customer Details
                </Typography>
                {isMSPUser && allowedRoles.includes(userRole) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleOpenCustomerModal}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      px: 2,
                      py: 0.75,
                    }}
                    startIcon={<i className="mdi mdi-account-plus" style={{ fontSize: "16px" }} />}
                  >
                    Add Customer
                  </Button>
                )}
              </Box>

              {/* Enhanced Search Field */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0); // Reset to first page when searching
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 400,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.875rem",
                      backgroundColor: "white",
                      borderRadius: "6px",
                      "& fieldset": {
                        borderColor: "#e2e8f0",
                        borderWidth: "1px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b82f6",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                    },
                  }}
                />
              </Box>
              <TableContainer
                sx={{
                  overflowX: "auto",
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f5f9",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#94a3b8",
                    },
                  },
                }}
              >
                <Table size="small" sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          backgroundColor: "#f8fafc",
                          borderRight: "1px solid #e2e8f0",
                          boxShadow: "2px 0 4px rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        Customer Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="The total cost of your AWS usage calculated strictly using AWS public list prices. It excludes all discounts/markup, credits, negotiated rates, Savings Plans, Reserved Instances, and Enterprise Agreements."
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>AWS Usage (List Price)</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="The partner's (MSP's) actual AWS usage cost, after applying partner program discounts/markup, credits, and incentives."
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>MSP Wholesale Cost</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="The customer's AWS usage cost after the Partner (MSP) discount/markup is applied to the AWS list price."
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>Customer Usage</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="Represents the profit the Partner (MSP) earns on AWS infrastructure resold to the customer."
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>MSP Margin</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="Custom service charges applied by the MSP for additional professional services"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>Professional Services</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="Includes customer usage charge, marketplace subscription charges and additional 3rd party charges."
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>Customer Spend</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        Accounts
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "0.75rem",
                          py: 1,
                        }}
                      >
                        <Tooltip
                          title="Customer spend for the last 3 months"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>3-month spend</span>
                            <InfoOutlinedIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: "#94a3b8",
                                opacity: 0.6,
                                "&:hover": {
                                  opacity: 1,
                                  color: "#64748b",
                                },
                                transition: "all 0.2s ease",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {isMSPUser && allowedRoles.includes(userRole) && (
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "0.75rem",
                            py: 1,
                            position: "sticky",
                            right: 0,
                            zIndex: 3,
                            backgroundColor: "#f8fafc",
                            borderLeft: "1px solid #e2e8f0",
                            boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCustomers.map((customer, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                            "& td": {
                              backgroundColor: "#f8fafc",
                            },
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            py: 1,
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            backgroundColor: "white",
                            borderRight: "1px solid #e2e8f0",
                            boxShadow: "2px 0 4px rgba(0, 0, 0, 0.05)",
                            "&:hover": {
                              backgroundColor: "#f8fafc !important",
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {customer.customer_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatCurrency(customer.aws_usage_list_price ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatCurrency(customer.msp_wholesale_cost ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatCurrency(customer.customer_usage ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#059669",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatCurrency(customer.realized_margin ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatCurrency(customer.professional_service ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatCurrency(customer.total_customer_spend ?? 0)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          {(() => {
                            const accountCount = customer.account_numbers ? customer.account_numbers.length : 0;
                            const isDisabled = accountCount === 0;
                            
                            return (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: isDisabled ? "not-allowed" : "pointer",
                                  width: 32,
                                  height: 32,
                                  borderRadius: "50%",
                                  backgroundColor: isDisabled ? "#f3f4f6" : "#f0f9ff",
                                  border: isDisabled ? "1px solid #e2e8f0" : "1px solid #3b82f6",
                                  transition: "all 0.2s ease-in-out",
                                  opacity: isDisabled ? 0.6 : 1,
                                  ...(isDisabled ? {} : {
                                    "&:hover": {
                                      backgroundColor: "#dbeafe",
                                      borderColor: "#2563eb",
                                      boxShadow: "0 1px 3px rgba(59, 130, 246, 0.2)",
                                    },
                                  }),
                                }}
                                onClick={(e) => {
                                  if (!isDisabled) {
                                    e.stopPropagation();
                                    handleOpenAccountsModal(customer);
                                  }
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: isDisabled ? "#9ca3af" : "#3b82f6",
                                    fontSize: "0.75rem",
                                    fontFamily:
                                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                    lineHeight: 1,
                                  }}
                                >
                                  {accountCount}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 30,
                              cursor: "pointer",
                              borderRadius: "4px",
                              border: "1px solid transparent",
                              position: "relative",
                              "&:hover": {
                                backgroundColor: "#f1f5f9",
                                border: "1px solid #3b82f6",
                                boxShadow: "0 2px 4px rgba(59, 130, 246, 0.1)",
                                "&::after": {
                                  opacity: 1,
                                },
                              },
                              "&::after": {
                                content: '"📊"',
                                position: "absolute",
                                top: 2,
                                right: 2,
                                fontSize: "10px",
                                opacity: 0,
                                transition: "opacity 0.2s ease",
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                borderRadius: "3px",
                                padding: "2px 3px",
                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                              },
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => handleOpenChartDetail(customer)}
                            title="Click to view detailed spend trend chart"
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={getCustomerMiniChartData(customer)}
                              >
                                <XAxis dataKey="monthNumber" hide />
                                <Line
                                  type="monotone"
                                  dataKey="amount"
                                  stroke="#059669"
                                  strokeWidth={1.5}
                                  dot={false}
                                  activeDot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                        </TableCell>
                        {isMSPUser && allowedRoles.includes(userRole) && (
                          <TableCell
                            sx={{
                              py: 1,
                              position: "sticky",
                              right: 0,
                              zIndex: 2,
                              backgroundColor: "white",
                              borderLeft: "1px solid #e2e8f0",
                              boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.05)",
                              "&:hover": {
                                backgroundColor: "#f8fafc !important",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Tooltip title={`Go to Cloud Spend Dashboard for ${customer.customer_name}`} arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleLoginToOrganization(customer.orgcode, customer.customer_name)}
                                  sx={{
                                    color: "#3b82f6",
                                    borderRadius: "50%",
                                    border: "1px solid #3b82f6",
                                    padding: "4px",
                                    "&:hover": {
                                      backgroundColor: "#dbeafe",
                                      color: "#1d4ed8",
                                      borderColor: "#1d4ed8",
                                    },
                                  }}
                                >
                                  <LoginIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip
                                title={
                                  customer?.is_customer === true
                                    ? "Delete customer"
                                    : "Please email support.cloudcatcher@cloudnovatech.com to request deletion of this Customer."
                                }
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDeleteDialog(customer)}
                                    disabled={customer?.is_customer !== true}
                                    sx={{
                                      color: customer?.is_customer === true ? "#dc2626" : "#d1d5db",
                                      "&:hover": {
                                        backgroundColor:
                                          customer?.is_customer === true ? "#fee2e2" : "transparent",
                                        color: customer?.is_customer === true ? "#991b1b" : "#d1d5db",
                                      },
                                      "&.Mui-disabled": {
                                        color: "#d1d5db",
                                      },
                                      padding: "4px",
                                    }}
                                  >
                                    <i className="mdi mdi-delete" style={{ fontSize: "18px" }} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Compact Pagination */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderTop: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                  minHeight: 40,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", fontSize: "0.75rem" }}
                >
                  {filteredCustomers.length > 0
                    ? `${page * rowsPerPage + 1}–${Math.min(
                        (page + 1) * rowsPerPage,
                        filteredCustomers.length
                      )} of ${filteredCustomers.length}`
                    : "0 of 0"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleChangePage(null, page - 1)}
                    disabled={page === 0}
                    sx={{
                      padding: "4px",
                      color: page === 0 ? "#d1d5db" : "#64748b",
                      "&:hover": {
                        backgroundColor: page === 0 ? "transparent" : "#f1f5f9",
                      },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleChangePage(null, page + 1)}
                    disabled={
                      page >=
                      Math.ceil(filteredCustomers.length / rowsPerPage) - 1
                    }
                    sx={{
                      padding: "4px",
                      color:
                        page >=
                        Math.ceil(filteredCustomers.length / rowsPerPage) - 1
                          ? "#d1d5db"
                          : "#64748b",
                      "&:hover": {
                        backgroundColor:
                          page >=
                          Math.ceil(filteredCustomers.length / rowsPerPage) - 1
                            ? "transparent"
                            : "#f1f5f9",
                      },
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Chart Detail Panel */}
      <ChartDetailPanel
        open={chartDetailOpen}
        onClose={handleCloseChartDetail}
        customer={selectedCustomer}
        chartData={
          selectedCustomer ? getCustomerMiniChartData(selectedCustomer) : []
        }
      />

      {/* Customer Modal */}
      <CustomerModal
        open={customerModalOpen}
        onClose={handleCloseCustomerModal}
        onSuccess={handleCustomerSuccess}
        organizations={accounts}
      />

      {/* Delete Customer Dialog */}
      <DeleteCustomerDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        customer={customerToDelete}
        onSuccess={handleDeleteSuccess}
      />

      {/* Customer Accounts Modal */}
      <CustomerAccountsModal
        open={accountsModalOpen}
        onClose={handleCloseAccountsModal}
        customer={selectedCustomerForAccounts}
      />

      {/* Footnotes */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#64748b", fontSize: "0.75rem", lineHeight: 1.5 }}
        >
          • YTD = Year to date
          <br />• N/A = Not available
        </Typography>
      </Box>
    </Box>
  );
};

export default MarginManagement;
